import { unsplashImageSrcSet } from '../utils/unsplashImageSrcSet';

/** Same generic id returned for every plan/city until the app uploads a real cover. */
export const API_PLACEHOLDER_PHOTO_IDS = new Set([
  '1562883676-8c7feb83f09b',
]);

export type PlanPhotoGroup = 'food' | 'wellness' | 'sport' | 'outdoor' | 'social';

type PlanImageMeta = {
  image: string;
  imagePosition: string;
};

/**
 * User-curated bundled covers (see public/plan-cover-picker.html).
 * hash(slug) % pool.length rotates within each group.
 */
const PLAN_GROUP_IMAGES: Record<PlanPhotoGroup, PlanImageMeta[]> = {
  food: [
    { image: '/images/plans/food-wine-toast.jpg', imagePosition: '50% 42%' },
    { image: '/images/plans/food-restaurant.jpg', imagePosition: '50% 45%' },
    { image: '/images/plans/food-pizza.jpg', imagePosition: '50% 50%' },
    { image: '/images/plans/food-cafe-wine.jpg', imagePosition: '50% 40%' },
  ],
  wellness: [
    { image: '/images/home-redesign/chat/plans/2-barre.jpg', imagePosition: '50% 35%' },
    { image: '/images/plans/wellness-pilates.jpg', imagePosition: '50% 45%' },
    { image: '/images/plans/wellness-gym-women.jpg', imagePosition: '50% 40%' },
  ],
  sport: [
    { image: '/images/plans/sport-yoga-outdoor.jpg', imagePosition: '50% 40%' },
    { image: '/images/plans/sport-hiking-women.jpg', imagePosition: '50% 42%' },
    { image: '/images/plans/sport-trail-active.jpg', imagePosition: '50% 45%' },
  ],
  outdoor: [
    { image: '/images/home-redesign/chat/plans/4-sunset.jpg', imagePosition: '50% 35%' },
    { image: '/images/plans/outdoor-nature.jpg', imagePosition: '50% 45%' },
    { image: '/images/plans/outdoor-forest.jpg', imagePosition: '50% 40%' },
  ],
  social: [
    { image: '/images/home-redesign/cta-photo.jpg', imagePosition: '50% 40%' },
    { image: '/images/plans/social-event.jpg', imagePosition: '50% 40%' },
  ],
};

/** Most specific groups first — first keyword match wins. */
const PLAN_GROUP_RULES: { group: PlanPhotoGroup; keywords: string[] }[] = [
  {
    group: 'food',
    keywords: [
      'brunch', 'desayuno', 'merienda', 'comer', 'comida', 'cena', 'cenar',
      'restaurante', 'restaurant', 'tapas', 'sushi', 'korean', 'coreano',
      'cafe', 'coffee', 'vino', 'wine', 'copas', 'cerveza', 'beer',
      'tardeo', 'vermut', 'vermú', 'aperitivo', 'drinks', 'tomar algo',
      'gastro', 'pizza', 'pasta', 'cocina', 'tapa', 'bocadillo',
      'helado', 'postre', 'ramen', 'burger', 'cocinar',
    ],
  },
  {
    group: 'wellness',
    keywords: [
      'yoga', 'pilates', 'barre', 'gym', 'fitness', 'entrenar', 'entrenamiento',
      'stretching', 'meditacion', 'meditation', 'mindfulness', 'spa', 'wellness',
      'zumba', 'spinning', 'crossfit', 'gimnasio', 'wellbeing',
    ],
  },
  {
    group: 'sport',
    keywords: [
      'senderismo', 'hiking', 'trekking', 'ruta de', 'ruta en',
      'bici', 'bike', 'ciclismo', 'cycling', 'correr', 'running',
      'padel', 'tenis', 'tennis', 'natacion', 'swim', 'surf',
      'escalada', 'climbing', 'deporte', 'sport', 'partido',
    ],
  },
  {
    group: 'outdoor',
    keywords: [
      'parque', 'park', 'playa', 'beach', 'paseo', 'walk', 'pasear',
      'sunset', 'atardecer', 'amanecer', 'montana', 'mountain',
      'picnic', 'jardin', 'garden', 'naturaleza', 'nature',
      'aire libre', 'outdoor', 'trail', 'lago', 'lake', 'verano', 'summer',
    ],
  },
  {
    group: 'social',
    keywords: [
      'quedada', 'conocer', 'charla', 'afterwork', 'terraza', 'terrace',
      'networking', 'meetup', 'social', 'festival', 'fiesta', 'party',
      'amapiano', 'afrobeat', 'musica', 'music', 'noche', 'night',
      'vibes', 'quedar', 'hangout',
    ],
  },
];

export type PlanImageSources = {
  src: string;
  srcSet?: string;
  imagePosition: string;
  group: PlanPhotoGroup;
};

function normalizePlanText(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

export function isPlaceholderPlanPhotoId(photoId?: string | null): boolean {
  if (!photoId) return true;
  return API_PLACEHOLDER_PHOTO_IDS.has(photoId);
}

export function classifyPlanGroup(planName: string): PlanPhotoGroup {
  const text = normalizePlanText(planName);

  for (const { group, keywords } of PLAN_GROUP_RULES) {
    if (keywords.some((keyword) => text.includes(normalizePlanText(keyword)))) {
      return group;
    }
  }

  return 'social';
}

function pickGroupImage(group: PlanPhotoGroup, slug: string): PlanImageMeta {
  const pool = PLAN_GROUP_IMAGES[group];
  const index = hashString(slug) % pool.length;
  return pool[index];
}

export function getPlanImageSources(input: {
  slug: string;
  name: string;
  photoId?: string;
  imagePosition?: string;
}): PlanImageSources {
  if (!isPlaceholderPlanPhotoId(input.photoId) && input.photoId) {
    const { src, srcSet } = unsplashImageSrcSet(input.photoId);
    return {
      src,
      srcSet,
      imagePosition: input.imagePosition ?? '50% 50%',
      group: classifyPlanGroup(input.name),
    };
  }

  const group = classifyPlanGroup(input.name);
  const meta = pickGroupImage(group, input.slug);

  return {
    src: meta.image,
    imagePosition: meta.imagePosition,
    group,
  };
}

/** Absolute URL for OG tags when using a bundled /public asset. */
export function planImageAbsoluteUrl(src: string): string {
  if (src.startsWith('http://') || src.startsWith('https://')) {
    return src;
  }
  return `https://chisme.social${src.startsWith('/') ? src : `/${src}`}`;
}
